// Every string BidCard can render, and the shape a caller may replace them with.
//
// The card never chooses a language. It is handed one object of finished text —
// one language's worth, already resolved — and renders it; when the app's locale
// changes the page rebuilds the object and passes a new one, and the card
// re-renders without knowing why.
//
// It lives beside the component rather than inside it so a test can import the
// defaults and assert the namespace covers every key the card can render, and so
// an app can read the key list without reaching into an SFC. It is deliberately
// NOT exported from the package barrel: the catalog gate holds that a barrel
// exports components and nothing else.
//
// A PARTIAL object is valid and is the normal case — the card merges over these
// defaults key by key, so a half-written namespace falls back rather than
// leaving holes. Missing keys are fine. An UNKNOWN key is the case worth
// catching: `moneyLabels` for `moneyLabel` supplies nothing the card reads, and
// without a validator the card renders English forever with nothing saying why.

/**
 * The English defaults, frozen.
 *
 * `{name}` placeholders are filled by the card. A translation may move them
 * around its sentence or drop one it does not need; it may not invent one, since
 * the card only substitutes the names it knows.
 */
export const BID_CARD_TRANSLATIONS = Object.freeze({
  // The header. `run` labels the dealer-facing number — the one place the card
  // does not say asset, because the number is the position the auction house
  // assigned in the run order.
  run: 'Run',
  // The clock, by magnitude rather than by sale type. Units collapse as the
  // window grows, so a three-hour timed asset and an asset at the wire both fit
  // the one line whose height cannot change.
  secondsLeft: '{seconds}s left',
  minutesLeft: '{minutes}m {seconds}s',
  closesIn: 'Closes in {hours}h {minutes}m',
  // Read out beside a countdown resting on zero. Reaching zero is not the asset
  // closing — the runtime decides that and its frame arrives after — so the card
  // is waiting rather than stalled, and the pulse says so to everyone else.
  waitingOnResult: 'Waiting on the result',

  // The money. The word beneath the figure is fixed: a current bid, an opening
  // ask and a final price are never on screen together, so what kind of number
  // it is lives in the headline instead.
  moneyLabel: 'current',
  // Visually hidden, beside the target figure — the flag glyph is unambiguous
  // for anyone who can see it and says nothing to a screen reader.
  target: 'Target {amount}',

  // The annotation row.
  notes: 'NOTES',
  liked: 'LIKED',
  // Visually hidden. Colored letters are not self-describing.
  lights: 'Lights: {lights}',
  // FIVE, matching AUCTION_LIGHT_CODES and BidSheet's catalog. It held four
  // while the card filtered white out before drawing; AuctionLights draws every
  // code it resolves, so a fifth name is one the sentence can now need.
  lightNames: Object.freeze({ R: 'Red', G: 'Green', Y: 'Yellow', B: 'Blue', W: 'White' }),

  // The thumbnail's accessible name. The title is also the asset's one line of
  // text, so the two cannot disagree.
  openDetail: 'View {title}',

  // The action row.
  bid: 'Bid',
  // The 96px chip, where the label sits ABOVE the figure. These three read as a
  // caption over a value and only work stacked — set beside each other they
  // compose "Proxy Set", which is not a phrase in any language.
  proxy: 'Proxy',
  // No amount armed reads as an invitation rather than as zero.
  proxyUnset: 'Set',
  proxySpent: 'SPENT',
  // The same control with the whole row to itself, which is one line rather than
  // two — so it needs its own words rather than the chip's laid end to end. The
  // separator is part of the string because where it goes is the translation's
  // decision: French Canadian sets a space either side of a middot, and a
  // language that would rather write "Mandat : 15 000 $" can.
  proxyWide: 'Proxy · {amount}',
  proxyWideSpent: 'Spent · {amount}',
  proxyWideUnset: 'Set proxy',
  // The bid control while a live proxy is armed. Disabled rather than absent, so
  // the dealer's thumb keeps one position per action.
  proxyBidding: 'Proxy is bidding',
  // The bid control while the dealer holds the high bid. Disabled for the same
  // reason and by the same rule as the line above — a bid here would raise their
  // own price — and it outranks that line, because leading is the stronger fact.
  winningBid: 'Winning',
  // `disabled: {}` is a complete card — a real state, not a fallback nobody
  // looked at.
  bidUnavailable: 'Bidding unavailable',
});

/**
 * Fill `{name}` placeholders. Unknown names are left alone rather than blanked,
 * so a translation carrying a placeholder the card does not supply reads as the
 * mistake it is instead of losing a word silently.
 */
export function interpolate(template, values) {
  return String(template).replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match
  );
}

/**
 * The keys in `translations` that BidCard would not read, as dotted paths.
 *
 * Two failures, one list: a key the defaults do not have, and a key whose type
 * disagrees with the default's. Both render as English forever, and neither
 * shows up anywhere.
 *
 * Non-empty means the object is rejected — this IS the prop validator, exported
 * separately so a test can assert on the paths rather than on a boolean.
 */
export function unreadTranslationKeys(translations, defaults = BID_CARD_TRANSLATIONS, path = '') {
  if (translations === null || typeof translations !== 'object') {
    return [path || '(root)'];
  }

  return Object.entries(translations).flatMap(([key, value]) => {
    const address = path ? `${path}.${key}` : key;

    if (!Object.prototype.hasOwnProperty.call(defaults, key)) return [address];

    const expected = defaults[key];
    if (typeof expected === 'object') {
      return unreadTranslationKeys(value, expected, address);
    }
    return typeof value === typeof expected ? [] : [address];
  });
}

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) return BID_CARD_TRANSLATIONS;

  return {
    ...BID_CARD_TRANSLATIONS,
    ...translations,
    lightNames: { ...BID_CARD_TRANSLATIONS.lightNames, ...(translations.lightNames ?? {}) },
  };
}

/**
 * The default money format: Canadian dollars, whole dollars, English grouping.
 *
 * Built once rather than per call. A caller that needs another locale passes its
 * own `formatMoney` — a function rather than a locale tag, because given `fr-CA`
 * the card would have to build the CAD, narrow-symbol, no-cents policy itself,
 * and so would every other component that shows money.
 */
const defaultMoneyFormat = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
});

export const formatMoneyDefault = (amount) => defaultMoneyFormat.format(amount);

/**
 * The `i18n` prop's validator: `{ translations, formatMoney }`, both optional.
 *
 * Missing keys are what the merge is for. This catches the two things the merge
 * cannot: a key the card never reads, and a `formatMoney` that is not callable.
 */
export function isValidBidCardI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') return false;
  // `translation` for `translations` fails exactly the way a mis-keyed string
  // does — silently, in English — so the outer object is closed too.
  if (Object.keys(i18n).some((key) => key !== 'translations' && key !== 'formatMoney')) return false;
  if (i18n.formatMoney !== undefined && typeof i18n.formatMoney !== 'function') return false;
  if (i18n.translations === undefined) return true;
  return unreadTranslationKeys(i18n.translations).length === 0;
}
